/**
 * Typed fetch helpers for the employee handbook. Mirrors the two controllers under
 * /api/employee-handbook/* — the HR authoring routes and the two an employee may call.
 */
import { DEFAULT_DOC_KEY, type DocKey } from '@system/shared'
import type {
  ApiResponse,
  HandbookAckStateDto,
  HandbookAckStatusDto,
  HandbookChapterDto,
  HandbookChapterReorderInput,
  HandbookChapterUpsertInput,
  HandbookCurrentDto,
  HandbookDraftDto,
  HandbookPublishInput,
  HandbookQuizCreateInput,
  HandbookQuizCreatedDto,
  HandbookQuizOptionsDto,
  HandbookReleaseSummary,
  HandbookRemindResult,
  HandbookReorderInput,
  HandbookSectionDto,
  HandbookSectionUpsertInput,
} from '@system/shared'
import { apiFetch } from './api-client-core'

const BASE = '/api/employee-handbook'
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/**
 * Văn bản đang thao tác, gắn vào mọi route soạn thảo.
 *
 * Ghi rõ cả khi là sổ tay: một lời gọi không nói mình sửa văn bản nào thì lúc
 * đọc log không phân biệt được "cố tình sổ tay" với "quên truyền".
 */
const doc = (docKey: DocKey) => `?doc=${encodeURIComponent(docKey)}`

// ---- HR authoring ----

export async function getHandbookDraft(docKey: DocKey): Promise<ApiResponse<HandbookDraftDto>> {
  return apiFetch<HandbookDraftDto>(`${BASE}/draft${doc(docKey)}`)
}

export async function createHandbookChapter(
  docKey: DocKey,
  input: HandbookChapterUpsertInput,
): Promise<ApiResponse<HandbookChapterDto>> {
  return apiFetch<HandbookChapterDto>(`${BASE}/chapters${doc(docKey)}`, {
    method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input),
  })
}

export async function updateHandbookChapter(
  docKey: DocKey,
  id: string,
  input: HandbookChapterUpsertInput,
): Promise<ApiResponse<HandbookChapterDto>> {
  return apiFetch<HandbookChapterDto>(`${BASE}/chapters/${encodeURIComponent(id)}${doc(docKey)}`, {
    method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(input),
  })
}

export async function deleteHandbookChapter(
  docKey: DocKey,
  id: string,
): Promise<ApiResponse<{ id: string }>> {
  return apiFetch<{ id: string }>(`${BASE}/chapters/${encodeURIComponent(id)}${doc(docKey)}`, {
    method: 'DELETE',
  })
}

export async function createHandbookSection(
  docKey: DocKey,
  input: HandbookSectionUpsertInput,
): Promise<ApiResponse<HandbookSectionDto>> {
  return apiFetch<HandbookSectionDto>(`${BASE}/sections${doc(docKey)}`, {
    method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input),
  })
}

export async function updateHandbookSection(
  docKey: DocKey,
  id: string,
  input: HandbookSectionUpsertInput,
): Promise<ApiResponse<HandbookSectionDto>> {
  return apiFetch<HandbookSectionDto>(`${BASE}/sections/${encodeURIComponent(id)}${doc(docKey)}`, {
    method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(input),
  })
}

export async function deleteHandbookSection(
  docKey: DocKey,
  id: string,
): Promise<ApiResponse<{ id: string }>> {
  return apiFetch<{ id: string }>(`${BASE}/sections/${encodeURIComponent(id)}${doc(docKey)}`, {
    method: 'DELETE',
  })
}

export async function reorderHandbookChapters(
  docKey: DocKey,
  input: HandbookChapterReorderInput,
): Promise<ApiResponse<{ ok: true }>> {
  return apiFetch<{ ok: true }>(`${BASE}/chapters/reorder${doc(docKey)}`, {
    method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(input),
  })
}

export async function reorderHandbookSections(
  docKey: DocKey,
  input: HandbookReorderInput,
): Promise<ApiResponse<{ ok: true }>> {
  return apiFetch<{ ok: true }>(`${BASE}/sections/reorder${doc(docKey)}`, {
    method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(input),
  })
}

export async function publishHandbook(
  docKey: DocKey,
  input: HandbookPublishInput,
): Promise<ApiResponse<HandbookReleaseSummary>> {
  return apiFetch<HandbookReleaseSummary>(`${BASE}/publish${doc(docKey)}`, {
    method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input),
  })
}

/** Bộ câu hỏi có sẵn + đề đã có của bản mới nhất — dữ liệu cho cửa sổ tạo bài kiểm tra. */
export async function getHandbookQuizOptions(docKey: DocKey): Promise<ApiResponse<HandbookQuizOptionsDto>> {
  return apiFetch<HandbookQuizOptionsDto>(`${BASE}/quiz-options${doc(docKey)}`)
}

/** Tạo đề cho một bản phát hành. Idempotent: bản đã có đề thì trả về đề đó. */
export async function createHandbookQuiz(
  docKey: DocKey,
  input: HandbookQuizCreateInput,
): Promise<ApiResponse<HandbookQuizCreatedDto>> {
  return apiFetch<HandbookQuizCreatedDto>(`${BASE}/quiz${doc(docKey)}`, {
    method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input),
  })
}

export async function getHandbookAckStatus(): Promise<ApiResponse<HandbookAckStatusDto>> {
  return apiFetch<HandbookAckStatusDto>(`${BASE}/ack-status`)
}

export async function remindHandbookReaders(
  eCodes: string[],
): Promise<ApiResponse<HandbookRemindResult>> {
  return apiFetch<HandbookRemindResult>(`${BASE}/remind`, {
    method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ eCodes }),
  })
}

// ---- employee read side ----

/** HR-only: the unpublished draft, shaped exactly like what employees read. */
export async function getHandbookPreview(docKey: DocKey): Promise<ApiResponse<HandbookCurrentDto>> {
  return apiFetch<HandbookCurrentDto>(`${BASE}/preview${doc(docKey)}`)
}

export async function getHandbookCurrent(
  docKey: DocKey = DEFAULT_DOC_KEY,
): Promise<ApiResponse<HandbookCurrentDto>> {
  return apiFetch<HandbookCurrentDto>(`${BASE}/current${doc(docKey)}`)
}

/** Nag decision only — the popup calls this on every page load, so it must stay small. */
export async function getHandbookAckState(): Promise<ApiResponse<HandbookAckStateDto>> {
  return apiFetch<HandbookAckStateDto>(`${BASE}/ack-state`)
}

export async function acknowledgeHandbook(shownVersion: number): Promise<ApiResponse<{ ok: true }>> {
  return apiFetch<{ ok: true }>(`${BASE}/ack`, {
    method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ shownVersion }),
  })
}
